import { supabase } from "../lib/supabaseClient";
import { apiEndpoints } from "../config/environment";
import {
  Quote,
  TimeEntry,
  LaborRole,
  Kit,
  PriceListItem,
  Customer,
  TermsTemplate,
  PdfExportSettings,
  Invoice,
  Subcontractor,
  CompanyInfo,
  AIChatMessage,
  AIActionProposal,
  DailyLogData,
  VoiceNote,
} from "../types";
import type {
  Receipt,
  ReceiptCostCategory,
  ReceiptTagMapping,
  ReceiptTaxProfile,
} from "../types/receipts";
import type { AppActionLog } from "./dailyLogActionTracker";
import {
  DEMO_QUOTE,
  DEFAULT_KITS,
  DEFAULT_CUSTOMERS,
  LABOR_ROLES,
  DEFAULT_MARKUP,
  DEFAULT_PST_FACTOR,
  DEFAULT_PDF_SETTINGS,
  TERMS_LIBRARY,
  DEFAULT_COMPANY_INFO,
  DEFAULT_VALIDITY_DAYS,
} from "../constants";
import { APP_CONFIG } from "../constants/app";
import { generateId } from "../utils/idGenerator";
import { serviceLogger } from "./logging";

const log = serviceLogger("Storage");

// --- DEFAULT INITIALIZERS ---
const getDefaults = () => ({
  quotes: [DEMO_QUOTE],
  time: [] as TimeEntry[],
  roles: [...LABOR_ROLES],
  kits: DEFAULT_KITS,
  priceList: [] as PriceListItem[],
  customers: DEFAULT_CUSTOMERS,
  markup: DEFAULT_MARKUP,
  pst: DEFAULT_PST_FACTOR,
  pdf: DEFAULT_PDF_SETTINGS,
  validityDays: DEFAULT_VALIDITY_DAYS,
  companyInfo: DEFAULT_COMPANY_INFO,
  terms: [
    { id: "ind", name: "Industrial", text: TERMS_LIBRARY["INDUSTRIAL"] },
    { id: "mun", name: "Municipal", text: TERMS_LIBRARY["MUNICIPAL"] },
    { id: "res", name: "Residential", text: TERMS_LIBRARY["RESI_COMM"] },
  ],
  invoices: [] as Invoice[],
  subcontractors: [] as Subcontractor[],
});

// --- LOCAL STORAGE KEYS ---
const KEYS = {
  QUOTES: "oler_quotes",
  TIME: "oler_time_entries",
  INVOICES: "oler_invoices",
  CUSTOMERS: "oler_customers",
  ROLES: "oler_labor_roles",
  SETTINGS: "oler_global_settings",
  COMPANY: "oler_company_info",
  KITS: "oler_kits",
  PRICELIST: "oler_price_list",
  TERMS: "oler_terms_templates",
  SUBCONTRACTORS: "oler_subcontractors",
  AI_CHAT_HISTORY: "oler_ai_chat_history",
  AI_ACTION_HISTORY: "oler_ai_action_history",
  AI_ACTIONS: "oler_ai_actions",
  SUPPLIER_DOC_SETTINGS: "oler_supplier_doc_settings",
  RECEIPTS: "oler_receipts",
  RECEIPT_TAG_MAPPINGS: "oler_receipt_tag_mappings",
  RECEIPT_COST_CATEGORIES: "oler_receipt_cost_categories",
  RECEIPT_TAX_PROFILES: "oler_receipt_tax_profiles",
  RECEIPT_SYNC_EVENTS: "oler_receipt_sync_events",
};

const STORAGE_META_KEY = "oler_storage_meta_v1";
const CONCURRENCY_TABLES = new Set(["quotes", "invoices", "customers", "time_entries"]);

// --- HELPERS ---

// Storage result type for structured error reporting
export type StorageResult = {
  success: boolean;
  error?: "auth" | "network" | "server" | "validation" | "unknown";
  message?: string;
};

type AIActionHistoryEntry = {
  id?: string;
  action: AIActionProposal;
  executedAt: number;
  result: "success" | "error";
};

type StorageMetaCache = Record<string, Record<string, number>>;

// Check if D1 storage is available
const isD1Available = (): boolean => {
  return apiEndpoints.storageWorkerUrl !== null;
};

const normalizePriceListItems = (list: PriceListItem[]): PriceListItem[] => {
  return list.map((item) => ((item as any).id ? item : { ...item, id: generateId() }));
};

const normalizeActionHistory = (history: AIActionHistoryEntry[]): AIActionHistoryEntry[] => {
  return history.map((entry) => ((entry as any).id ? entry : { ...entry, id: generateId() }));
};

const getMetaCache = (): StorageMetaCache => {
  return getLocal(STORAGE_META_KEY, {} as StorageMetaCache);
};

const saveMetaCache = (cache: StorageMetaCache) => {
  saveLocal(STORAGE_META_KEY, cache);
};

const stripMetaFromItem = <T extends Record<string, any>>(item: T): Omit<T, "__meta"> => {
  if (!item || typeof item !== "object") {
    return item;
  }
  if (!Object.prototype.hasOwnProperty.call(item, "__meta")) {
    return item;
  }
  const { __meta: _meta, ...rest } = item;
  return rest as Omit<T, "__meta">;
};

const stripMetaFromItems = <T extends Record<string, any>>(items: T[]): Omit<T, "__meta">[] => {
  return items.map((item) => stripMetaFromItem(item));
};

const updateMetaCacheFromItems = (table: string, items: any[]) => {
  if (!CONCURRENCY_TABLES.has(table)) {
    return;
  }
  const cache = getMetaCache();
  const tableCache = { ...(cache[table] ?? {}) };
  let updated = false;

  items.forEach((item) => {
    const id = item?.id;
    const updatedAt = item?.__meta?.updatedAt;
    if (id && typeof updatedAt === "number") {
      if (tableCache[id] !== updatedAt) {
        tableCache[id] = updatedAt;
        updated = true;
      }
    }
  });

  if (updated) {
    cache[table] = tableCache;
    saveMetaCache(cache);
  }
};

const updateMetaCacheFromUpdates = (
  table: string,
  updates: Array<{ id: string; updatedAt: number }>
) => {
  if (!CONCURRENCY_TABLES.has(table)) {
    return;
  }
  const cache = getMetaCache();
  const tableCache = { ...(cache[table] ?? {}) };
  let changed = false;

  updates.forEach((update) => {
    if (update?.id && typeof update.updatedAt === "number") {
      if (tableCache[update.id] !== update.updatedAt) {
        tableCache[update.id] = update.updatedAt;
        changed = true;
      }
    }
  });

  if (changed) {
    cache[table] = tableCache;
    saveMetaCache(cache);
  }
};

const attachExpectedUpdatedAt = (table: string, items: Array<Record<string, any>>) => {
  if (!CONCURRENCY_TABLES.has(table)) {
    return items;
  }
  const cache = getMetaCache();
  const tableCache = cache[table] ?? {};

  return items.map((item) => {
    if (!item || typeof item !== "object") {
      return item;
    }
    const id = item.id;
    const expectedUpdatedAt = id ? tableCache[id] : undefined;
    if (typeof expectedUpdatedAt === "number") {
      return { ...item, expectedUpdatedAt };
    }
    return item;
  });
};

const dailyLogUpdateQueues: Map<string, Promise<unknown>> = new Map();

const buildDailyLogDefaults = (date: string): DailyLogData => ({
  date,
  voiceNotes: [],
  appActions: [],
  lastSyncedAt: Date.now(),
});

const enqueueDailyLogUpdate = (
  date: string,
  task: () => Promise<DailyLogData>
): Promise<DailyLogData> => {
  const previous: Promise<unknown> = dailyLogUpdateQueues.get(date) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(task);
  dailyLogUpdateQueues.set(
    date,
    next.catch(() => undefined)
  );
  return next;
};

const getTrimmedDailyLogText = (value: unknown): string => {
  return typeof value === "string" ? value.trim() : "";
};

const buildDailyLogExtractionContent = (data: DailyLogData): string => {
  const voiceNoteLines = (data.voiceNotes || [])
    .map((vn: VoiceNote) => {
      const canonicalText = getTrimmedDailyLogText(vn.text);
      if (canonicalText) {
        return canonicalText;
      }
      const legacyText = getTrimmedDailyLogText((vn as { transcription?: unknown }).transcription);
      return legacyText;
    })
    .filter(Boolean)
    .map((text) => `[Voice Note] ${text}`);

  const actionLines = (data.appActions || [])
    .map((action: AppActionLog) => {
      const canonicalDescription = getTrimmedDailyLogText(action.description);
      if (canonicalDescription) {
        return canonicalDescription;
      }
      const legacySummary = getTrimmedDailyLogText((action as { summary?: unknown }).summary);
      return legacySummary;
    })
    .filter(Boolean)
    .map((text) => `[Action] ${text}`);

  return [...voiceNoteLines, ...actionLines].join("\n\n");
};

// Helper to detect authentication failures from response
const detectAuthFailure = (response: Response, text: string): boolean => {
  // Check for HTTP auth error codes
  if (response.status === 401 || response.status === 403) {
    return true;
  }

  // Check if response is HTML (likely Cloudflare Access login page)
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("text/html")) {
    // Check for Cloudflare Access indicators
    if (
      text.includes("<!DOCTYPE") ||
      text.includes("<html") ||
      text.includes("cloudflare") ||
      text.includes("access") ||
      text.includes("login") ||
      text.includes("authenticate")
    ) {
      return true;
    }
  }

  return false;
};

// Helper to fetch data from D1
const fetchD1Data = async (
  table: string
): Promise<{ data: any[] | null; result: StorageResult }> => {
  if (!isD1Available()) {
    return {
      data: null,
      result: { success: false, error: "unknown", message: "D1 not available" },
    };
  }

  try {
    const url = `${apiEndpoints.storageWorkerUrl}/${table}`;
    const response = await fetch(url, {
      credentials: "include", // Include Cloudflare Access cookies
    });

    if (!response.ok) {
      const text = await response.text();

      // Detect authentication failures
      if (detectAuthFailure(response, text)) {
        return {
          data: null,
          result: {
            success: false,
            error: "auth",
            message: `Authentication required (${response.status})`,
          },
        };
      }

      // If we get HTML, the function isn't deployed or URL is wrong
      if (text.includes("<!DOCTYPE") || text.includes("<html")) {
        log.warn(
          `Storage function not found at ${url}. Check if VITE_STORAGE_WORKER_URL is set correctly.`
        );
        return {
          data: null,
          result: {
            success: false,
            error: "server",
            message: "Storage function not found",
          },
        };
      }

      log.warn(`Storage request failed for ${table}`, { status: response.status, text });
      return {
        data: null,
        result: {
          success: false,
          error: "server",
          message: `Server error: ${response.status}`,
        },
      };
    }

    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      const text = await response.text();
      log.warn(`Expected JSON but got ${contentType} for ${table}`, {
        preview: text.substring(0, 100),
      });
      return {
        data: null,
        result: {
          success: false,
          error: "server",
          message: "Invalid response format",
        },
      };
    }

    const payload = await response.json();
    const items = Array.isArray(payload)
      ? payload
      : payload && Array.isArray((payload as { data?: unknown }).data)
        ? (payload as { data: any[] }).data
        : [];

    updateMetaCacheFromItems(table, items);
    const strippedItems = CONCURRENCY_TABLES.has(table) ? stripMetaFromItems(items) : items;
    return {
      data: strippedItems,
      result: { success: true },
    };
  } catch (error) {
    log.error(`Error fetching from D1 ${table}`, error);
    return {
      data: null,
      result: {
        success: false,
        error: "network",
        message: error instanceof Error ? error.message : "Network error",
      },
    };
  }
};

// Helper to save data to D1
const saveD1Data = async (
  table: string,
  items: any[],
  _fullSync: boolean = false
): Promise<StorageResult> => {
  if (!isD1Available()) {
    return { success: false, error: "unknown", message: "D1 not available" };
  }

  try {
    const url = `${apiEndpoints.storageWorkerUrl}/${table}`;
    const strippedItems = CONCURRENCY_TABLES.has(table) ? stripMetaFromItems(items) : items;
    const payloadItems = attachExpectedUpdatedAt(
      table,
      strippedItems as Array<Record<string, any>>
    );
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payloadItems),
    });

    if (!response.ok) {
      if (response.status === 409) {
        log.warn(`Storage conflict detected for ${table}`, { status: response.status });
        window.dispatchEvent(
          new CustomEvent("storage-conflict", {
            detail: { table, message: "Conflict" },
          })
        );
        fetchD1Data(table).catch((error) => {
          log.warn(`Failed to refresh ${table} after conflict`, error);
        });
        return { success: false, error: "server", message: "Conflict" };
      }
      const text = await response.text();

      // Detect authentication failures
      if (detectAuthFailure(response, text)) {
        return {
          success: false,
          error: "auth",
          message: `Authentication required (${response.status})`,
        };
      }

      if (text.includes("<!DOCTYPE") || text.includes("<html")) {
        log.warn(
          `Storage function not found at ${url}. Check if VITE_STORAGE_WORKER_URL is set correctly.`
        );
        return {
          success: false,
          error: "server",
          message: "Storage function not found",
        };
      }

      log.warn(`Storage save failed for ${table}`, { status: response.status, text });
      return {
        success: false,
        error: "server",
        message: `Server error: ${response.status}`,
      };
    }

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const payload = await response.json();
      if (payload && Array.isArray(payload.updated)) {
        updateMetaCacheFromUpdates(
          table,
          payload.updated as Array<{ id: string; updatedAt: number }>
        );
      }
    }

    return { success: true };
  } catch (error) {
    log.error(`Error saving to D1 ${table}`, error);
    return {
      success: false,
      error: "network",
      message: error instanceof Error ? error.message : "Network error",
    };
  }
};

const deleteD1Item = async (table: string, id: string): Promise<StorageResult> => {
  if (!isD1Available()) return { success: true };

  try {
    const url = `${apiEndpoints.storageWorkerUrl}/${table}/${encodeURIComponent(id)}`;
    const response = await fetch(url, {
      method: "DELETE",
      credentials: "include",
    });

    if (!response.ok) {
      const text = await response.text();

      if (detectAuthFailure(response, text)) {
        return {
          success: false,
          error: "auth",
          message: `Authentication required (${response.status})`,
        };
      }

      log.warn(`Storage delete failed for ${table}`, { status: response.status, text });
      return {
        success: false,
        error: "server",
        message: `Server error: ${response.status}`,
      };
    }

    return { success: true };
  } catch (error) {
    log.error(`Error deleting from D1 ${table}`, error);
    return {
      success: false,
      error: "network",
      message: error instanceof Error ? error.message : "Network error",
    };
  }
};

function getPagesApiOriginFromStorage(): string {
  const fallbackOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const storageWorkerUrl = apiEndpoints.storageWorkerUrl;
  if (!storageWorkerUrl) return fallbackOrigin;
  try {
    return new URL(storageWorkerUrl, fallbackOrigin || undefined).origin;
  } catch {
    return fallbackOrigin;
  }
}

const deleteReceiptViaReceiptsApi = async (receiptId: string): Promise<StorageResult> => {
  try {
    const origin = getPagesApiOriginFromStorage();
    const url = `${origin}/api/receipts/${encodeURIComponent(receiptId)}/delete`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) {
      const text = await response.text();
      let payload: any = {};
      try {
        payload = text ? JSON.parse(text) : {};
      } catch {
        payload = {};
      }
      const message =
        typeof payload?.message === "string"
          ? payload.message
          : typeof payload?.error === "string"
            ? payload.error
            : `Server error: ${response.status}`;

      if (detectAuthFailure(response, text)) {
        return {
          success: false,
          error: "auth",
          message,
        };
      }

      return {
        success: false,
        error: "server",
        message,
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: "network",
      message: error instanceof Error ? error.message : "Network error",
    };
  }
};

// Helper to upsert data into Supabase tables
const upsertData = async (table: string, items: any[]) => {
  if (!supabase || items.length === 0) return;

  const payload = items.map((item) => ({
    id: item.id || item.key,
    data: item,
    updated_at: new Date().toISOString(),
  }));

  const { error } = await supabase.from(table).upsert(payload);
  if (error) log.error(`Error saving to ${table}`, error);
};

/**
 * Fetches all data from a Supabase table.
 *
 * Note: Currently fetches all records. For large datasets, consider implementing pagination.
 *
 * @param table - The Supabase table name to fetch from
 * @returns Array of data objects, or null if error or no Supabase connection
 *
 * @example
 * ```typescript
 * const quotes = await fetchData('quotes');
 * ```
 */
const fetchData = async (table: string) => {
  if (!supabase) return null;
  const { data, error } = await supabase.from(table).select("data");
  if (error) {
    log.error(`Error fetching from ${table}`, error);
    return null;
  }
  return data.map((row: any) => row.data);
};

// LocalStorage Helper
const getLocal = <T>(key: string, defaultVal: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultVal;
  } catch (e) {
    return defaultVal;
  }
};

// Enhanced localStorage quota management
const getStorageUsage = (): { used: number; quota: number; percentage: number } => {
  try {
    let used = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += localStorage[key].length + key.length;
      }
    }
    // Estimate quota (typically 5-10MB, but varies by browser)
    const quota = 5 * 1024 * 1024; // 5MB estimate
    return {
      used,
      quota,
      percentage: (used / quota) * 100,
    };
  } catch {
    return { used: 0, quota: 0, percentage: 0 };
  }
};

const cleanupOldData = (): boolean => {
  try {
    // Try to remove old/unused data if quota is exceeded
    // Remove old time entries (keep last MAX_TIME_ENTRIES)
    const timeEntries = getLocal(KEYS.TIME, []);
    if (timeEntries.length > APP_CONFIG.STORAGE.MAX_TIME_ENTRIES) {
      const sorted = timeEntries.sort(
        (a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      saveLocal(KEYS.TIME, sorted.slice(0, APP_CONFIG.STORAGE.MAX_TIME_ENTRIES));
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

const saveLocal = (key: string, data: any) => {
  try {
    const jsonData = JSON.stringify(data);
    localStorage.setItem(key, jsonData);
  } catch (e: any) {
    log.error("LocalStorage Save Error", e);

    if (
      e.name === "QuotaExceededError" ||
      e.message?.includes("quota") ||
      e.message?.includes("QUOTA")
    ) {
      // Try cleanup first
      const cleaned = cleanupOldData();

      if (cleaned) {
        // Retry after cleanup
        try {
          localStorage.setItem(key, JSON.stringify(data));
          log.info("Saved after cleanup");
          return;
        } catch (retryError) {
          // Still failed after cleanup
        }
      }

      // Get storage usage info
      const usage = getStorageUsage();
      const usedMB = (usage.used / (1024 * 1024)).toFixed(2);
      const percentage = usage.percentage.toFixed(1);

      // Show detailed error message
      const message =
        `Local storage is full (${usedMB}MB used, ${percentage}% of quota)!\n\n` +
        `Please:\n` +
        `1. Backup your data (Settings -> Data -> Download Backup)\n` +
        `2. Clear old quotes or time entries\n` +
        `3. Enable Cloud Sync if available\n\n` +
        `Your current changes were NOT saved.`;

      alert(message);

      // Throw error so caller knows save failed
      throw new Error("QUOTA_EXCEEDED");
    } else {
      // Other storage errors
      log.error("Unexpected storage error", e);
      alert("Failed to save data. Please try again or contact support.");
      throw e;
    }
  }
};

/**
 * Hybrid Storage Service
 *
 * Provides a unified interface for data persistence that automatically uses Supabase
 * when available, falling back to LocalStorage otherwise. This allows the app to work
 * both online (with cloud sync) and offline (with local storage).
 *
 * All operations are debounced to prevent excessive writes during rapid updates.
 *
 * @example
 * ```typescript
 * // Get quotes (works with both Supabase and LocalStorage)
 * const quotes = await StorageService.getQuotes();
 *
 * // Save quotes (automatically uses appropriate storage)
 * await StorageService.saveQuotes(quotes);
 * ```
 */
export const StorageService = {
  /**
   * Retrieves all quotes from storage.
   *
   * @returns Promise resolving to an array of Quote objects
   */
  getQuotes: async (): Promise<Quote[]> => {
    // Try D1 first
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("quotes");
      if (result.success && Array.isArray(data)) {
        // Also save to LocalStorage as backup
        const stripped = stripMetaFromItems(data as Quote[]);
        saveLocal(KEYS.QUOTES, stripped);
        // Dispatch success event to indicate auth is working
        window.dispatchEvent(new CustomEvent("storage-success"));
        return stripped;
      }
      // If auth error, still return local data but trigger warning
      if (result.error === "auth") {
        // Store auth failure for monitoring
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }
    // Fall back to Supabase
    if (supabase) {
      const data = await fetchData("quotes");
      if (Array.isArray(data)) {
        saveLocal(KEYS.QUOTES, data);
        window.dispatchEvent(new CustomEvent("storage-success"));
        return data;
      }
    }
    // Fall back to LocalStorage - ensure we always return an array
    const localDefaults = !isD1Available() && !supabase ? getDefaults().quotes : [];
    const localData = getLocal(KEYS.QUOTES, localDefaults);
    // Safety check: ensure we always return an array
    if (!Array.isArray(localData)) {
      log.warn("LocalStorage quotes data is not an array, resetting to defaults");
      const defaults = localDefaults;
      saveLocal(KEYS.QUOTES, defaults);
      return defaults;
    }
    return localData;
  },
  /**
   * Saves quotes to storage.
   *
   * Note: This operation is debounced (500ms) when called from the store to prevent
   * excessive writes during rapid updates.
   *
   * @param quotes - Array of Quote objects to save
   * @returns StorageResult indicating success/failure and error type
   */
  saveQuotes: async (quotes: Quote[]): Promise<StorageResult> => {
    // Always save to LocalStorage first (fast, local backup)
    const stripped = stripMetaFromItems(quotes as Quote[]);
    saveLocal(KEYS.QUOTES, stripped);

    // Then sync to cloud (D1 or Supabase)
    if (isD1Available()) {
      // CF-01: fullSync disabled - use explicit DELETE calls for deletions
      const result = await saveD1Data("quotes", stripped, false);
      // Dispatch events for auth failures/success so components can react
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return result;
    } else if (supabase) {
      // For Supabase, we need to handle deletions separately
      // Get all existing quotes
      const { data: existing } = await supabase.from("quotes").select("id");
      if (existing) {
        const existingIds = existing.map((r: any) => r.id);
        const newIds = quotes.map((q) => q.id);
        const toDelete = existingIds.filter((id: string) => !newIds.includes(id));

        // Delete removed quotes
        if (toDelete.length > 0) {
          await supabase.from("quotes").delete().in("id", toDelete);
        }
      }

      // Upsert all quotes
      await upsertData("quotes", quotes);
      window.dispatchEvent(new CustomEvent("storage-success"));
      return { success: true };
    }

    // LocalStorage only - still dispatch success since local save worked
    window.dispatchEvent(new CustomEvent("storage-success"));
    return { success: true };
  },

  // Time Entries
  getTimeEntries: async (): Promise<TimeEntry[]> => {
    if (isD1Available()) {
      const { data } = await fetchD1Data("time_entries");
      if (data) {
        const stripped = stripMetaFromItems(data as TimeEntry[]);
        saveLocal(KEYS.TIME, stripped);
        return stripped;
      }
    }
    if (supabase) {
      const data = await fetchData("time_entries");
      if (data) {
        saveLocal(KEYS.TIME, data);
        return data;
      }
    }
    return getLocal(KEYS.TIME, getDefaults().time);
  },
  saveTimeEntries: async (entries: TimeEntry[]): Promise<StorageResult> => {
    const stripped = stripMetaFromItems(entries as TimeEntry[]);
    saveLocal(KEYS.TIME, stripped);
    if (isD1Available()) {
      const result = await saveD1Data("time_entries", stripped, true);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(
          new CustomEvent("storage-success", { detail: { table: "time_entries" } })
        );
      }
      return result;
    } else if (supabase) {
      await upsertData("time_entries", entries);
      window.dispatchEvent(
        new CustomEvent("storage-success", { detail: { table: "time_entries" } })
      );
      return { success: true };
    }
    window.dispatchEvent(new CustomEvent("storage-success", { detail: { table: "time_entries" } }));
    return { success: true };
  },

  // Invoices
  getInvoices: async (): Promise<Invoice[]> => {
    if (isD1Available()) {
      const { data } = await fetchD1Data("invoices");
      if (data) {
        const stripped = stripMetaFromItems(data as Invoice[]);
        saveLocal(KEYS.INVOICES, stripped);
        return stripped;
      }
    }
    if (supabase) {
      const data = await fetchData("invoices");
      if (data) {
        saveLocal(KEYS.INVOICES, data);
        return data;
      }
    }
    return getLocal(KEYS.INVOICES, getDefaults().invoices);
  },
  saveInvoices: async (invoices: Invoice[]): Promise<StorageResult> => {
    const stripped = stripMetaFromItems(invoices as Invoice[]);
    saveLocal(KEYS.INVOICES, stripped);
    if (isD1Available()) {
      const result = await saveD1Data("invoices", stripped, false);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success", { detail: { table: "invoices" } }));
      }
      return result;
    } else if (supabase) {
      await upsertData("invoices", invoices);
      window.dispatchEvent(new CustomEvent("storage-success", { detail: { table: "invoices" } }));
      return { success: true };
    }
    window.dispatchEvent(new CustomEvent("storage-success", { detail: { table: "invoices" } }));
    return { success: true };
  },
  deleteInvoice: async (id: string): Promise<StorageResult> => {
    const invoices = getLocal(KEYS.INVOICES, getDefaults().invoices).filter(
      (inv: any) => inv?.id !== id
    );
    saveLocal(KEYS.INVOICES, invoices);

    const result = await deleteD1Item("invoices", id);
    if (result.error === "auth") {
      window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
    } else if (result.success) {
      window.dispatchEvent(new CustomEvent("storage-success", { detail: { table: "invoices" } }));
    }
    return result;
  },

  // Customers
  getCustomers: async (): Promise<Customer[]> => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("customers");
      if (result.success && Array.isArray(data)) {
        const stripped = stripMetaFromItems(data as Customer[]);
        saveLocal(KEYS.CUSTOMERS, stripped);
        return stripped;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }
    if (supabase) {
      const data = await fetchData("customers");
      if (Array.isArray(data)) {
        saveLocal(KEYS.CUSTOMERS, data);
        return data;
      }
    }
    const localDefaults = !isD1Available() && !supabase ? getDefaults().customers : [];
    return getLocal(KEYS.CUSTOMERS, localDefaults);
  },
  saveCustomers: async (customers: Customer[]) => {
    const stripped = stripMetaFromItems(customers as Customer[]);
    saveLocal(KEYS.CUSTOMERS, stripped);
    if (isD1Available()) {
      await saveD1Data("customers", stripped, true);
    } else if (supabase) {
      await upsertData("customers", stripped);
    }
  },

  deleteCustomer: async (id: string): Promise<StorageResult> => {
    if (isD1Available()) {
      const result = await deleteD1Item("customers", id);
      if (!result.success) {
        return result;
      }
    } else if (supabase) {
      await supabase.from("customers").delete().eq("id", id);
    }
    return { success: true };
  },

  // Subcontractors
  getSubcontractors: async (): Promise<Subcontractor[]> => {
    if (isD1Available()) {
      const { data } = await fetchD1Data("subcontractors");
      if (data && data.length > 0) {
        saveLocal(KEYS.SUBCONTRACTORS, data);
        return data;
      }
    }
    if (supabase) {
      const data = await fetchData("subcontractors");
      if (data && data.length > 0) {
        saveLocal(KEYS.SUBCONTRACTORS, data);
        return data;
      }
    }
    return getLocal(KEYS.SUBCONTRACTORS, getDefaults().subcontractors);
  },
  saveSubcontractors: async (subs: Subcontractor[]) => {
    saveLocal(KEYS.SUBCONTRACTORS, subs);
    if (isD1Available()) {
      await saveD1Data("subcontractors", subs);
    } else if (supabase) {
      await upsertData("subcontractors", subs);
    }
  },

  // --- SETTINGS ---

  // Labor Roles
  getLaborRoles: async (): Promise<LaborRole[]> => {
    if (isD1Available()) {
      const { data } = await fetchD1Data("labor_roles");
      if (data && data.length > 0) {
        saveLocal(KEYS.ROLES, data);
        return data;
      }
    }
    if (supabase) {
      const { data } = await supabase.from("settings").select("data").eq("key", "roles").single();
      if (data) {
        saveLocal(KEYS.ROLES, data.data);
        return data.data;
      }
    }
    return getLocal(KEYS.ROLES, getDefaults().roles);
  },
  saveLaborRoles: async (roles: LaborRole[]) => {
    saveLocal(KEYS.ROLES, roles);
    if (isD1Available()) {
      await saveD1Data("labor_roles", roles);
    } else if (supabase) {
      await supabase.from("settings").upsert({ key: "roles", data: roles, updated_at: new Date() });
    }
  },

  // Global Config
  getGlobalSettings: async () => {
    const defaults = {
      markup: getDefaults().markup,
      pst: getDefaults().pst,
      pdf: getDefaults().pdf,
      validityDays: getDefaults().validityDays,
      defaultContingencyPercent: 0.05,
    };

    // Try D1 first
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("settings");
      if (data && Array.isArray(data) && data.length > 0) {
        // Find the global_settings item
        const settingsItem = data.find((item: any) => item.id === "global_settings");
        if (settingsItem) {
          // Remove the id field since it's not part of the settings structure
          const { id, ...settings } = settingsItem;
          // Also save to LocalStorage as backup (without id)
          saveLocal(KEYS.SETTINGS, settings);
          // Dispatch success event to indicate auth is working
          window.dispatchEvent(new CustomEvent("storage-success"));
          return settings;
        }
      }
      // If auth error, still return local data but trigger warning
      if (result.error === "auth") {
        // Store auth failure for monitoring
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }

    // Fall back to LocalStorage
    return getLocal(KEYS.SETTINGS, defaults);
  },
  saveGlobalSettings: async (
    markup: number,
    pst: number,
    pdf: PdfExportSettings,
    validityDays: number,
    defaultContingencyPercent?: number
  ): Promise<StorageResult> => {
    const settings = { markup, pst, pdf, validityDays, defaultContingencyPercent };

    // Always save to LocalStorage first (fast, local backup)
    saveLocal(KEYS.SETTINGS, settings);

    // Then sync to cloud (D1)
    if (isD1Available()) {
      // Store settings as a single item with id='global_settings'
      // The storage API expects an array, so we wrap it
      const settingsItem = { id: "global_settings", ...settings };
      const result = await saveD1Data("settings", [settingsItem]);
      // Dispatch events for auth failures/success so components can react
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return result;
    }

    // LocalStorage only - still dispatch success since local save worked
    window.dispatchEvent(new CustomEvent("storage-success"));
    return { success: true };
  },

  // Company Info
  getCompanyInfo: async (): Promise<CompanyInfo> => {
    const defaults = getDefaults().companyInfo;

    // Try D1 first
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("settings");
      if (data && Array.isArray(data) && data.length > 0) {
        // Find the company_info item
        const companyItem = data.find((item: any) => item.id === "company_info");
        if (companyItem) {
          // Remove the id field since it's not part of the company info structure
          const { id, ...companyInfo } = companyItem;
          // Also save to LocalStorage as backup (without id)
          saveLocal(KEYS.COMPANY, companyInfo);
          // Dispatch success event to indicate auth is working
          window.dispatchEvent(new CustomEvent("storage-success"));
          return companyInfo;
        }
      }
      // If auth error, still return local data but trigger warning
      if (result.error === "auth") {
        // Store auth failure for monitoring
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }

    // Fall back to LocalStorage
    return getLocal(KEYS.COMPANY, defaults);
  },
  saveCompanyInfo: async (info: CompanyInfo): Promise<StorageResult> => {
    // Always save to LocalStorage first (fast, local backup)
    saveLocal(KEYS.COMPANY, info);

    // Then sync to cloud (D1)
    if (isD1Available()) {
      // Store company info as a single item with id='company_info'
      // The storage API expects an array, so we wrap it
      const companyItem = { id: "company_info", ...info };
      const result = await saveD1Data("settings", [companyItem]);
      // Dispatch events for auth failures/success so components can react
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return result;
    }

    // LocalStorage only - still dispatch success since local save worked
    window.dispatchEvent(new CustomEvent("storage-success"));
    return { success: true };
  },

  // Kits
  getKits: async (): Promise<Kit[]> => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("kits");
      if (result.success && Array.isArray(data)) {
        saveLocal(KEYS.KITS, data);
        return data;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }

    if (supabase) {
      const { data } = await supabase.from("settings").select("data").eq("key", "kits").single();
      if (data?.data) {
        saveLocal(KEYS.KITS, data.data);
        return data.data;
      }
    }

    const localDefaults = !isD1Available() && !supabase ? getDefaults().kits : [];
    return getLocal(KEYS.KITS, localDefaults);
  },
  saveKits: async (kits: Kit[]) => {
    saveLocal(KEYS.KITS, kits);

    if (isD1Available()) {
      const result = await saveD1Data("kits", kits, true);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return;
    }

    if (supabase) {
      await supabase.from("settings").upsert({ key: "kits", data: kits, updated_at: new Date() });
      window.dispatchEvent(new CustomEvent("storage-success"));
    }
  },

  // Price List
  getPriceList: async (): Promise<PriceListItem[]> => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("price_list_items");
      if (result.success && Array.isArray(data)) {
        const normalized = normalizePriceListItems(data);
        saveLocal(KEYS.PRICELIST, normalized);
        return normalized;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }

    if (supabase) {
      const { data } = await supabase
        .from("settings")
        .select("data")
        .eq("key", "pricelist")
        .single();
      if (data?.data) {
        saveLocal(KEYS.PRICELIST, data.data);
        return data.data;
      }
    }

    const localDefaults = !isD1Available() && !supabase ? getDefaults().priceList : [];
    const localData = getLocal(KEYS.PRICELIST, localDefaults);
    const normalized = normalizePriceListItems(localData || []);
    if (normalized.length !== (localData || []).length) {
      saveLocal(KEYS.PRICELIST, normalized);
    }
    return normalized;
  },
  savePriceList: async (list: PriceListItem[]) => {
    const normalized = normalizePriceListItems(list);
    saveLocal(KEYS.PRICELIST, normalized);

    if (isD1Available()) {
      const result = await saveD1Data("price_list_items", normalized, true);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return;
    }

    if (supabase) {
      await supabase
        .from("settings")
        .upsert({ key: "pricelist", data: normalized, updated_at: new Date() });
      window.dispatchEvent(new CustomEvent("storage-success"));
    }
  },

  // Terms
  getTermsTemplates: async (): Promise<TermsTemplate[]> => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("terms_templates");
      if (result.success && Array.isArray(data)) {
        saveLocal(KEYS.TERMS, data);
        return data;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }

    if (supabase) {
      const { data } = await supabase.from("settings").select("data").eq("key", "terms").single();
      if (data?.data) {
        saveLocal(KEYS.TERMS, data.data);
        return data.data;
      }
    }

    const localDefaults = !isD1Available() && !supabase ? getDefaults().terms : [];
    return getLocal(KEYS.TERMS, localDefaults);
  },
  saveTermsTemplates: async (templates: TermsTemplate[]) => {
    saveLocal(KEYS.TERMS, templates);

    if (isD1Available()) {
      const result = await saveD1Data("terms_templates", templates, true);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return;
    }

    if (supabase) {
      await supabase
        .from("settings")
        .upsert({ key: "terms", data: templates, updated_at: new Date() });
      window.dispatchEvent(new CustomEvent("storage-success"));
    }
  },

  // AI Chat History
  getAIChatHistory: async (): Promise<AIChatMessage[]> => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("ai_chat_history");
      if (result.success && Array.isArray(data)) {
        saveLocal(KEYS.AI_CHAT_HISTORY, data);
        return data;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }

    if (supabase) {
      const data = await fetchData("ai_chat_history");
      if (Array.isArray(data)) {
        saveLocal(KEYS.AI_CHAT_HISTORY, data);
        return data;
      }
    }

    return getLocal(KEYS.AI_CHAT_HISTORY, []);
  },
  saveAIChatHistory: async (history: AIChatMessage[]) => {
    // Limit to last 100 messages to prevent storage bloat
    const limitedHistory = history.slice(-100);
    saveLocal(KEYS.AI_CHAT_HISTORY, limitedHistory);

    if (isD1Available()) {
      const result = await saveD1Data("ai_chat_history", limitedHistory, true);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return;
    }

    if (supabase) {
      await upsertData("ai_chat_history", limitedHistory);
      window.dispatchEvent(new CustomEvent("storage-success"));
    }
  },

  // AI Action History
  getAIActionHistory: async (): Promise<
    Array<{ action: AIActionProposal; executedAt: number; result: "success" | "error" }>
  > => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("ai_action_history");
      if (result.success && Array.isArray(data)) {
        saveLocal(KEYS.AI_ACTION_HISTORY, data);
        return data;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }

    if (supabase) {
      const data = await fetchData("ai_action_history");
      if (Array.isArray(data)) {
        saveLocal(KEYS.AI_ACTION_HISTORY, data);
        return data;
      }
    }

    return getLocal(KEYS.AI_ACTION_HISTORY, []);
  },
  saveAIActionHistory: async (
    history: Array<{ action: AIActionProposal; executedAt: number; result: "success" | "error" }>
  ) => {
    // Limit to last 50 actions to prevent storage bloat
    const limitedHistory = history.slice(-50) as AIActionHistoryEntry[];
    const normalizedHistory = normalizeActionHistory(limitedHistory);
    saveLocal(KEYS.AI_ACTION_HISTORY, normalizedHistory);

    if (isD1Available()) {
      const result = await saveD1Data("ai_action_history", normalizedHistory, true);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return;
    }

    if (supabase) {
      await upsertData("ai_action_history", normalizedHistory);
      window.dispatchEvent(new CustomEvent("storage-success"));
    }
  },

  // AI Actions (pending proposals)
  getAIActions: async (): Promise<AIActionProposal[]> => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("ai_actions");
      if (result.success && Array.isArray(data)) {
        saveLocal(KEYS.AI_ACTIONS, data);
        return data;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }

    if (supabase) {
      const data = await fetchData("ai_actions");
      if (Array.isArray(data)) {
        saveLocal(KEYS.AI_ACTIONS, data);
        return data;
      }
    }

    return getLocal(KEYS.AI_ACTIONS, []);
  },
  saveAIActions: async (actions: AIActionProposal[]) => {
    // Limit to last 200 pending actions to avoid bloat
    const limited = actions.slice(-200);
    saveLocal(KEYS.AI_ACTIONS, limited);

    if (isD1Available()) {
      const result = await saveD1Data("ai_actions", limited, true);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return;
    }

    if (supabase) {
      await upsertData("ai_actions", limited);
      window.dispatchEvent(new CustomEvent("storage-success"));
    }
  },

  // Daily Log Data
  updateDailyLogData: async (
    date: string,
    updater: (current: DailyLogData) => Partial<DailyLogData> | Promise<Partial<DailyLogData>>
  ): Promise<DailyLogData> => {
    return enqueueDailyLogUpdate(date, async () => {
      const defaultData = buildDailyLogDefaults(date);
      const localKey = `dailyLogData_${date}`;
      const hasLocal =
        typeof localStorage !== "undefined" && localStorage.getItem(localKey) !== null;
      const current = hasLocal
        ? getLocal(localKey, defaultData)
        : await StorageService.getDailyLogData(date);

      const safeCurrent: DailyLogData = {
        ...defaultData,
        ...current,
        date,
        voiceNotes: Array.isArray(current?.voiceNotes)
          ? current.voiceNotes
          : defaultData.voiceNotes,
        appActions: Array.isArray(current?.appActions)
          ? current.appActions
          : defaultData.appActions,
      };

      const updated = (await updater(safeCurrent)) || {};
      const hasVoiceNotes = Object.prototype.hasOwnProperty.call(updated, "voiceNotes");
      const hasAppActions = Object.prototype.hasOwnProperty.call(updated, "appActions");
      const hasProcessedResults = Object.prototype.hasOwnProperty.call(updated, "processedResults");
      const hasLastProcessedAt = Object.prototype.hasOwnProperty.call(updated, "lastProcessedAt");

      const merged: DailyLogData = {
        ...safeCurrent,
        ...updated,
        date,
        voiceNotes: hasVoiceNotes
          ? Array.isArray(updated.voiceNotes)
            ? updated.voiceNotes
            : []
          : safeCurrent.voiceNotes,
        appActions: hasAppActions
          ? Array.isArray(updated.appActions)
            ? updated.appActions
            : []
          : safeCurrent.appActions,
        processedResults: hasProcessedResults
          ? updated.processedResults
          : safeCurrent.processedResults,
        lastProcessedAt: hasLastProcessedAt ? updated.lastProcessedAt : safeCurrent.lastProcessedAt,
        lastSyncedAt: updated.lastSyncedAt ?? Date.now(),
      };

      await StorageService.saveDailyLogData(date, merged);
      return merged;
    });
  },
  getDailyLogData: async (date: string): Promise<DailyLogData> => {
    const defaultData = buildDailyLogDefaults(date);

    // Try D1 first
    if (isD1Available()) {
      try {
        // Special fetch for daily_logs table (query by date)
        const url = `${apiEndpoints.storageWorkerUrl}/daily_logs?date=${date}`;
        const response = await fetch(url, {
          credentials: "include", // Include Cloudflare Access cookies
        });

        if (response.ok) {
          const data = await response.json();
          if (data && Array.isArray(data) && data.length > 0) {
            const dailyLog = data[0]; // Should only be one result for specific date
            if (dailyLog && dailyLog.data) {
              const parsed =
                typeof dailyLog.data === "string" ? JSON.parse(dailyLog.data) : dailyLog.data;
              // Ensure it has required fields
              const result: DailyLogData = {
                ...defaultData,
                ...parsed,
                date, // Ensure date matches
              };
              // Also save to LocalStorage as backup
              saveLocal(`dailyLogData_${date}`, result);
              return result;
            }
          }
        } else {
          const text = await response.text();
          // Detect authentication failures (Cloudflare Access, etc.)
          if (detectAuthFailure(response, text)) {
            window.dispatchEvent(
              new CustomEvent("storage-auth-failure", {
                detail: {
                  success: false,
                  error: "auth",
                  message: `Authentication required (${response.status})`,
                },
              })
            );
          } else if (text.includes("<!DOCTYPE") || text.includes("<html")) {
            log.warn(
              `Storage function not found at ${url}. Check if VITE_STORAGE_WORKER_URL is set correctly.`
            );
          } else {
            log.warn(`Storage request failed for daily_logs`, { status: response.status, text });
          }
        }
      } catch (error) {
        log.error("Error fetching daily log data from D1", error);
      }
    }

    // Fall back to Supabase
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from("daily_logs")
          .select("data")
          .eq("date", date)
          .single();

        if (!error && data && data.data) {
          const parsed = typeof data.data === "string" ? JSON.parse(data.data) : data.data;
          saveLocal(`dailyLogData_${date}`, parsed);
          return parsed;
        }
      } catch (error) {
        log.error("Error fetching daily log data from Supabase", error);
      }
    }

    // Fall back to LocalStorage
    return getLocal(`dailyLogData_${date}`, defaultData);
  },

  saveDailyLogData: async (date: string, data: DailyLogData): Promise<void> => {
    // Always save to LocalStorage first (fast, local backup)
    saveLocal(`dailyLogData_${date}`, data);

    // Then sync to cloud (D1 or Supabase)
    if (isD1Available()) {
      try {
        // D1 stores as array of { date, data } where date is primary key
        const url = `${apiEndpoints.storageWorkerUrl}/daily_logs`;
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // Include Cloudflare Access cookies
          body: JSON.stringify([{ date, data }]), // Send as array for batch insert
        });

        if (!response.ok) {
          const text = await response.text();
          if (detectAuthFailure(response, text)) {
            window.dispatchEvent(
              new CustomEvent("storage-auth-failure", {
                detail: {
                  success: false,
                  error: "auth",
                  message: `Authentication required (${response.status})`,
                },
              })
            );
          } else if (text.includes("<!DOCTYPE") || text.includes("<html")) {
            log.warn(
              `Storage function not found at ${url}. Check if VITE_STORAGE_WORKER_URL is set correctly.`
            );
          } else {
            log.warn(`Failed to save daily log data to D1`, { status: response.status, text });
          }
        } else {
          // Phase 1.5: Enqueue extraction job (non-blocking, fire-and-forget)
          // Only enqueue if there's content to extract (voice notes or app actions)
          if (
            (data.voiceNotes && data.voiceNotes.length > 0) ||
            (data.appActions && data.appActions.length > 0)
          ) {
            const logContent = buildDailyLogExtractionContent(data);

            if (logContent.trim()) {
              // Fire-and-forget: don't await, catch errors silently
              fetch(`${apiEndpoints.storageWorkerUrl}/extraction/enqueue`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                  jobType: "daily_log",
                  sourceId: date, // Use date as unique identifier for daily logs
                  payload: {
                    logContent,
                    logDate: date,
                  },
                }),
              }).catch((err) => {
                // Silent failure - extraction is non-critical
                log.debug("Failed to enqueue extraction job (non-blocking)", err);
              });
            }
          }
        }
      } catch (error) {
        log.error("Error saving daily log data to D1", error);
      }
    } else if (supabase) {
      try {
        await supabase.from("daily_logs").upsert({
          date,
          data: JSON.stringify(data),
          updated_at: new Date().toISOString(),
        });
      } catch (error) {
        log.error("Error saving daily log data to Supabase", error);
      }
    }
  },

  // Supplier Document Settings
  getSupplierDocumentSettings: async (): Promise<{
    emailLookbackDays: number;
    autoLinkConfidenceThreshold: number;
    enabled: boolean;
  }> => {
    const defaults = {
      emailLookbackDays: 7,
      autoLinkConfidenceThreshold: 0.8,
      enabled: true,
    };

    if (isD1Available()) {
      const { data, result } = await fetchD1Data("settings");
      if (result.success && Array.isArray(data)) {
        const settingsItem = data.find((item: any) => item.id === "supplier_doc_settings");
        if (settingsItem) {
          const { id, ...settings } = settingsItem;
          const merged = { ...defaults, ...settings };
          saveLocal(KEYS.SUPPLIER_DOC_SETTINGS, merged);
          return merged;
        }
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }

    if (supabase) {
      const { data } = await supabase
        .from("settings")
        .select("data")
        .eq("key", "supplier_doc_settings")
        .single();
      if (data) {
        const merged = { ...defaults, ...data.data };
        saveLocal(KEYS.SUPPLIER_DOC_SETTINGS, merged);
        return merged;
      }
    }
    return getLocal(KEYS.SUPPLIER_DOC_SETTINGS, defaults);
  },

  saveSupplierDocumentSettings: async (settings: {
    emailLookbackDays: number;
    autoLinkConfidenceThreshold: number;
    enabled: boolean;
  }) => {
    saveLocal(KEYS.SUPPLIER_DOC_SETTINGS, settings);

    if (isD1Available()) {
      const result = await saveD1Data("settings", [{ id: "supplier_doc_settings", ...settings }]);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return;
    }

    if (supabase) {
      await supabase
        .from("settings")
        .upsert({ key: "supplier_doc_settings", data: settings, updated_at: new Date() });
      window.dispatchEvent(new CustomEvent("storage-success"));
    }
  },

  // Receipts (MVP)
  getReceipts: async (): Promise<Receipt[]> => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("receipts");
      if (result.success && Array.isArray(data)) {
        saveLocal(KEYS.RECEIPTS, data);
        return data;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }
    return getLocal(KEYS.RECEIPTS, []);
  },

  saveReceipts: async (receipts: Receipt[]): Promise<StorageResult> => {
    saveLocal(KEYS.RECEIPTS, receipts);
    if (isD1Available()) {
      const result = await saveD1Data("receipts", receipts);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return result;
    }
    window.dispatchEvent(new CustomEvent("storage-success"));
    return { success: true };
  },

  deleteReceipt: async (id: string): Promise<StorageResult> => {
    const receipts = getLocal(KEYS.RECEIPTS, []).filter((r: any) => r?.id !== id);
    saveLocal(KEYS.RECEIPTS, receipts);

    const result = isD1Available()
      ? await deleteReceiptViaReceiptsApi(id)
      : await deleteD1Item("receipts", id);
    if (result.error === "auth") {
      window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
    } else if (result.success) {
      window.dispatchEvent(new CustomEvent("storage-success", { detail: { table: "receipts" } }));
    }
    return result;
  },

  getReceiptTagMappings: async (): Promise<ReceiptTagMapping[]> => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("receipt_tag_mappings");
      if (result.success && Array.isArray(data)) {
        saveLocal(KEYS.RECEIPT_TAG_MAPPINGS, data);
        return data;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }
    return getLocal(KEYS.RECEIPT_TAG_MAPPINGS, []);
  },

  saveReceiptTagMappings: async (mappings: ReceiptTagMapping[]): Promise<StorageResult> => {
    saveLocal(KEYS.RECEIPT_TAG_MAPPINGS, mappings);
    if (isD1Available()) {
      const result = await saveD1Data("receipt_tag_mappings", mappings);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return result;
    }
    window.dispatchEvent(new CustomEvent("storage-success"));
    return { success: true };
  },

  getReceiptCostCategories: async (): Promise<ReceiptCostCategory[]> => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("receipt_cost_categories");
      if (result.success && Array.isArray(data)) {
        saveLocal(KEYS.RECEIPT_COST_CATEGORIES, data);
        return data;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }
    return getLocal(KEYS.RECEIPT_COST_CATEGORIES, []);
  },

  saveReceiptCostCategories: async (categories: ReceiptCostCategory[]): Promise<StorageResult> => {
    saveLocal(KEYS.RECEIPT_COST_CATEGORIES, categories);
    if (isD1Available()) {
      const result = await saveD1Data("receipt_cost_categories", categories);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return result;
    }
    window.dispatchEvent(new CustomEvent("storage-success"));
    return { success: true };
  },

  getReceiptTaxProfiles: async (): Promise<ReceiptTaxProfile[]> => {
    if (isD1Available()) {
      const { data, result } = await fetchD1Data("receipt_tax_profiles");
      if (result.success && Array.isArray(data)) {
        saveLocal(KEYS.RECEIPT_TAX_PROFILES, data);
        return data;
      }
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      }
    }
    return getLocal(KEYS.RECEIPT_TAX_PROFILES, []);
  },

  saveReceiptTaxProfiles: async (profiles: ReceiptTaxProfile[]): Promise<StorageResult> => {
    saveLocal(KEYS.RECEIPT_TAX_PROFILES, profiles);
    if (isD1Available()) {
      const result = await saveD1Data("receipt_tax_profiles", profiles);
      if (result.error === "auth") {
        window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
      } else if (result.success) {
        window.dispatchEvent(new CustomEvent("storage-success"));
      }
      return result;
    }
    window.dispatchEvent(new CustomEvent("storage-success"));
    return { success: true };
  },
};
